import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { Income, Expense, ExpenseSection } from "../lib/types";
import { expenseUtils, incomeUtils } from "../lib";
import { pageShareAPI, PageShareInfo } from "../services/api/api";
import { SearchFilterBar } from "../components/common";
import IncomeList from "../components/income/IncomeList";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0f0f0f",
      paper: "#1a1a1a",
    },
    primary: {
      main: "#3182CE",
    },
  },
});

const formatDate = (value?: Date | string | null): string => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const SharedIncomePage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<PageShareInfo | null>(null);
  const [password, setPassword] = useState("");
  const [income, setIncome] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [incomeShareSettings, setIncomeShareSettings] = useState<{
    hiddenIncomeIds?: string[];
    hiddenSections?: ExpenseSection[];
  }>({ hiddenIncomeIds: [], hiddenSections: [] });
  const [isLive, setIsLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [paidFilter, setPaidFilter] = useState<"all" | "paid" | "unpaid">(
    "all"
  );
  const [recurringFilter, setRecurringFilter] = useState<
    "all" | "recurring" | "one-time"
  >("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [expandedSection, setExpandedSection] = useState<ExpenseSection | null>(
    null
  );

  const handleSearchApply = useCallback(() => {
    setAppliedSearchQuery(searchQuery);
    console.debug("[SharedIncome] Search applied:", searchQuery);
  }, [searchQuery]);

  const loadShareInfo = useCallback(async () => {
    if (!shareId) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }

    try {
      console.debug("[SharedIncome] Loading share info:", shareId);
      const info = await pageShareAPI.getInfo(shareId);
      if (info.pageType !== "income") {
        setError("This share link is not for income.");
        setShareInfo(null);
        return;
      }
      setShareInfo(info);
      console.debug("[SharedIncome] Share info loaded:", info);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load share");
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  const validateShare = useCallback(
    async (providedPassword?: string) => {
      if (!shareId) return;
      setValidating(true);
      setError(null);
      try {
        console.debug("[SharedIncome] Validating share:", shareId);
        const result = await pageShareAPI.validate(shareId, providedPassword);
        const normalizedIncome = (result.income || []).map((item) => {
          const normalized = { ...item };
          if (typeof normalized.expectedByDate === "string") {
            normalized.expectedByDate = new Date(normalized.expectedByDate);
          }
          if (typeof normalized.invoiceTotal === "string") {
            normalized.invoiceTotal = parseFloat(normalized.invoiceTotal);
          }
          if (typeof normalized.paidToDate === "string") {
            normalized.paidToDate = parseFloat(normalized.paidToDate);
          }
          if (typeof normalized.receivedDate === "string") {
            normalized.receivedDate = new Date(normalized.receivedDate);
          }
          if (typeof normalized.depositDate === "string") {
            normalized.depositDate = new Date(normalized.depositDate);
          }
          if (!normalized.type) {
            normalized.type = "Actual";
          }
          return normalized;
        });
        const normalizedExpenses = (result.expenses || []).map((expense) => {
          const normalized = { ...expense };
          if (typeof normalized.dueDate === "string") {
            normalized.dueDate = new Date(normalized.dueDate);
          }
          if (typeof normalized.amount === "string") {
            normalized.amount = parseFloat(normalized.amount);
          }
          return normalized;
        });
        setIncome(normalizedIncome);
        setExpenses(normalizedExpenses);
        setIncomeShareSettings(result.payload?.incomeSettings || {});
        setIsLive(result.isLive);
        setIsAuthenticated(true);
        console.debug("[SharedIncome] Share validated:", {
          income: result.income?.length || 0,
          expenses: result.expenses?.length || 0,
          isLive: result.isLive,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Validation failed";
        setError(message);
        console.error("[SharedIncome] Validation failed:", message);
      } finally {
        setValidating(false);
      }
    },
    [shareId]
  );

  useEffect(() => {
    loadShareInfo();
  }, [loadShareInfo]);

  useEffect(() => {
    if (!shareInfo) return;
    if (shareInfo.requiresPassword) return;
    validateShare();
  }, [shareInfo, validateShare]);

  const handleSubmitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password.trim()) return;
    await validateShare(password.trim());
  };

  const clients = useMemo(() => {
    const values = new Set<string>();
    income.forEach((item) => {
      if (item.client) values.add(item.client);
    });
    return ["all", ...Array.from(values).sort()];
  }, [income]);

  const filteredIncome = useMemo(() => {
    const query = appliedSearchQuery.trim().toLowerCase();
    const hiddenIds = new Set(incomeShareSettings.hiddenIncomeIds || []);
    const matchesQuery = (item: Income) => {
      if (!query) return true;
      return [
        item.client,
        item.invoiceNumber,
        item.notes,
        item.paymentProcessor,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query));
    };

    const matchesPaid = (item: Income) => {
      if (paidFilter === "all") return true;
      return paidFilter === "paid" ? item.isPaid : !item.isPaid;
    };

    const matchesRecurring = (item: Income) => {
      if (recurringFilter === "all") return true;
      return recurringFilter === "recurring"
        ? item.isRecurring
        : !item.isRecurring;
    };

    const matchesClient = (item: Income) => {
      if (clientFilter === "all") return true;
      return item.client === clientFilter;
    };

    const next = income.filter(
      (item) =>
        !hiddenIds.has(item.id) &&
        matchesQuery(item) &&
        matchesPaid(item) &&
        matchesRecurring(item) &&
        matchesClient(item)
    );

    console.debug("[SharedIncome] Filtered income:", {
      total: income.length,
      filtered: next.length,
    });

    return next;
  }, [
    income,
    appliedSearchQuery,
    paidFilter,
    recurringFilter,
    clientFilter,
    incomeShareSettings.hiddenIncomeIds,
  ]);

  const prospectiveIncome = useMemo(
    () =>
      filteredIncome.filter(
        (entry) => (entry.type || "Actual") === "Prospective"
      ),
    [filteredIncome]
  );
  const nonProspectiveIncome = useMemo(
    () =>
      filteredIncome.filter(
        (entry) => (entry.type || "Actual") !== "Prospective"
      ),
    [filteredIncome]
  );

  // Categorize filtered income into sections for IncomeList
  const categorizedIncome = useMemo(() => {
    const base = incomeUtils.categorizeIncome(nonProspectiveIncome);
    const hiddenSections = incomeShareSettings.hiddenSections || [];
    const next = { ...base };
    hiddenSections.forEach((section) => {
      next[section] = [];
    });
    return next;
  }, [incomeShareSettings.hiddenSections, nonProspectiveIncome]);

  const sectionTotals = useMemo(
    () => incomeUtils.calculateIncomeSectionTotals(categorizedIncome),
    [categorizedIncome]
  );

  const activeExpenses = useMemo(
    () => expenses.filter((exp) => !exp.isSkipped),
    [expenses]
  );
  const categorizedExpenses = useMemo(
    () => expenseUtils.categorizeExpenses(expenses),
    [expenses]
  );
  const categorizedActiveExpenses = useMemo(
    () => expenseUtils.categorizeExpenses(activeExpenses),
    [activeExpenses]
  );
  const expenseSectionTotals = useMemo(
    () => expenseUtils.calculateSectionTotals(categorizedActiveExpenses),
    [categorizedActiveExpenses]
  );
  const expenseTotalsBySection = useMemo(() => {
    const totals: Record<string, number> = {};
    expenseSectionTotals.forEach((s) => {
      totals[s.section] = s.total;
    });
    return totals;
  }, [expenseSectionTotals]);

  const filteredSections = useMemo(() => {
    const hiddenSections = new Set(incomeShareSettings.hiddenSections || []);
    return sectionTotals.filter(
      (s) =>
        !hiddenSections.has(s.section) &&
        s.section !== "upcoming" &&
        s.section !== "thisMonth" &&
        s.section !== "nextMonth"
    );
  }, [sectionTotals, incomeShareSettings.hiddenSections]);

  const currentMonthTotal = useMemo(
    () => incomeUtils.getCurrentMonthIncomeTotal(filteredIncome),
    [filteredIncome]
  );
  const currentMonthExpenseTotal = useMemo(
    () => expenseUtils.getCurrentMonthExpensesTotal(activeExpenses),
    [activeExpenses]
  );
  const visibleSections = useMemo(
    () => [
      { section: "total" as const, total: currentMonthTotal },
      ...filteredSections,
    ],
    [currentMonthTotal, filteredSections]
  );

  const getSectionColor = (section: string) => {
    switch (section) {
      case "total":
        return "#3182CE";
      case "received":
        return "#9CA3AF";
      case "overdue":
        return "#3182CE";
      case "today":
        return "#9AE6B4";
      case "tomorrow":
        return "#68D391";
      case "thisWeek":
        return "#48BB78";
      case "next10Days":
        return "#38A169";
      case "thisMonth":
        return "#2F855A";
      case "nextMonth":
        return "#276749";
      case "expected":
        return "#22543D";
      default:
        return "#48BB78";
    }
  };

  const getSectionLabel = (section: string) => {
    switch (section) {
      case "total":
        return "Total";
      case "received":
        return "Received";
      case "overdue":
        return "Overdue";
      case "today":
        return "Today";
      case "tomorrow":
        return "Tomorrow";
      case "thisWeek":
        return "Within 7 Days";
      case "next10Days":
        return "Within 10 Days";
      case "expected":
        return "Within 30 Days";
      default:
        return section;
    }
  };

  // Compute date range from income
  const dateRange = useMemo(() => {
    if (filteredIncome.length === 0) return null;
    let earliest: Date | null = null;
    let latest: Date | null = null;
    filteredIncome.forEach((item) => {
      if (!item.expectedByDate) return;
      const d =
        item.expectedByDate instanceof Date
          ? item.expectedByDate
          : new Date(item.expectedByDate);
      if (Number.isNaN(d.getTime())) return;
      if (!earliest || d < earliest) earliest = d;
      if (!latest || d > latest) latest = d;
    });
    if (!earliest || !latest) return null;
    return { earliest, latest };
  }, [filteredIncome]);

  // No-op callbacks for read-only IncomeList
  const noop = useCallback(() => {}, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0f0f0f", p: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ color: "white", mb: 0.5 }}>
              Shared Income
            </Typography>
            {dateRange && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                {formatDate(dateRange.earliest)} —{" "}
                {formatDate(dateRange.latest)}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {isLive && (
                <Chip label="Live data" color="primary" size="small" />
              )}
              {shareInfo?.requiresPassword && (
                <Chip label="Password" color="warning" size="small" />
              )}
              {shareInfo?.requiresAuth && (
                <Chip label="Auth required" color="secondary" size="small" />
              )}
              {shareInfo?.expiresAt && (
                <Chip
                  label={`Expires ${formatDate(shareInfo.expiresAt)}`}
                  size="small"
                />
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : shareInfo?.requiresPassword && !isAuthenticated ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <Card sx={{ maxWidth: 420, width: "100%" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <LockIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      Password Protected
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Enter the password to view this income.
                    </Typography>
                  </Box>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <form onSubmit={handleSubmitPassword}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{ mb: 2 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={validating || !password.trim()}
                    >
                      {validating ? (
                        <CircularProgress size={24} />
                      ) : (
                        "View Income"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Box>
          ) : error && !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
            </Box>
          ) : !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Box sx={{ maxWidth: 480, textAlign: "center" }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This link requires authentication. Please log in to view this
                  income.
                </Alert>
                <Button variant="contained" href="/login">
                  Log In
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                    lg: `repeat(${visibleSections.length}, 1fr)`,
                  },
                  gap: 2,
                  mb: 3,
                }}
              >
                {visibleSections.map((section) => {
                  const isTotal = section.section === "total";
                  const expenseTotal = isTotal
                    ? currentMonthExpenseTotal
                    : expenseTotalsBySection[section.section] || 0;
                  const netValue = section.total - expenseTotal;
                  const isPositive = netValue >= 0;
                  const sectionExpenses = isTotal
                    ? expenseUtils.getCurrentMonthExpenses(expenses)
                    : categorizedExpenses[section.section] || [];
                  const isSelected =
                    !isTotal && expandedSection === section.section;

                  const expenseTooltipContent =
                    sectionExpenses.length > 0 ? (
                      <Box sx={{ maxWidth: 320 }}>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 600,
                            mb: 0.5,
                            borderBottom: "1px solid #444",
                            pb: 0.5,
                          }}
                        >
                          Expenses (
                          {sectionExpenses.filter((e) => !e.isSkipped).length}{" "}
                          active)
                        </Typography>
                        {sectionExpenses
                          .filter((e) => !e.isSkipped)
                          .slice(0, 6)
                          .map((exp) => {
                            const dueDate =
                              exp.dueDate instanceof Date
                                ? exp.dueDate
                                : new Date(exp.dueDate);
                            const dueDateStr = `${(dueDate.getUTCMonth() + 1).toString().padStart(2, "0")}/${dueDate.getUTCDate().toString().padStart(2, "0")}`;
                            return (
                              <Box
                                key={exp.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: "11px",
                                  py: 0.25,
                                  gap: 1,
                                }}
                              >
                                <span
                                  style={{ color: "#9CA3AF", flexShrink: 0 }}
                                >
                                  {dueDateStr}
                                </span>
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {exp.name}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    flexShrink: 0,
                                    color: "#E53E3E",
                                  }}
                                >
                                  -{expenseUtils.formatCurrency(exp.amount)}
                                </span>
                              </Box>
                            );
                          })}
                      </Box>
                    ) : (
                      "No expenses"
                    );

                  return (
                    <Box
                      key={section.section}
                      onClick={
                        isTotal
                          ? undefined
                          : () => {
                              setExpandedSection((prev) =>
                                prev === section.section
                                  ? null
                                  : (section.section as ExpenseSection)
                              );
                              console.debug(
                                "[SharedIncome] Card click",
                                section.section
                              );
                            }
                      }
                      sx={{
                        backgroundColor: "#1a1a1a",
                        padding: "16px",
                        borderRadius: "8px",
                        boxShadow: isSelected
                          ? `0 0 0 2px ${getSectionColor(section.section)}`
                          : "0 1px 2px rgba(0,0,0,0.05)",
                        borderTop: `4px solid ${getSectionColor(
                          section.section
                        )}`,
                        cursor: isTotal ? "default" : "pointer",
                        transition:
                          "transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out",
                        "&:hover": isTotal
                          ? {}
                          : {
                              transform: "translateY(-2px)",
                              boxShadow: isSelected
                                ? `0 4px 6px rgba(0,0,0,0.1), 0 0 0 2px ${getSectionColor(section.section)}`
                                : "0 4px 6px rgba(0,0,0,0.1)",
                            },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#718096",
                          marginBottom: "4px",
                        }}
                      >
                        {getSectionLabel(section.section)}
                      </Typography>
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                        {incomeUtils.formatCurrency(section.total)}
                      </Typography>
                      {expenseTotal > 0 && (
                        <Tooltip
                          title={expenseTooltipContent}
                          arrow
                          placement="bottom"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "#1a1a1a",
                                "& .MuiTooltip-arrow": { color: "#1a1a1a" },
                              },
                            },
                          }}
                        >
                          <Typography
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: isPositive ? "#38A169" : "#E53E3E",
                              marginTop: "4px",
                              display: "inline-block",
                            }}
                          >
                            {isPositive ? "+" : ""}
                            {incomeUtils.formatCurrency(netValue)}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <SearchFilterBar
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                onSearchApply={handleSearchApply}
                searchPlaceholder="Search client or invoice"
                filters={[
                  {
                    key: "paid",
                    label: "Paid",
                    value: paidFilter,
                    onChange: (value) =>
                      setPaidFilter(value as "all" | "paid" | "unpaid"),
                    options: [
                      { value: "all", label: "All" },
                      { value: "paid", label: "Paid" },
                      { value: "unpaid", label: "Unpaid" },
                    ],
                  },
                  {
                    key: "recurring",
                    label: "Recurring",
                    value: recurringFilter,
                    onChange: (value) =>
                      setRecurringFilter(
                        value as "all" | "recurring" | "one-time"
                      ),
                    options: [
                      { value: "all", label: "All" },
                      { value: "recurring", label: "Recurring" },
                      { value: "one-time", label: "One-time" },
                    ],
                  },
                  {
                    key: "client",
                    label: "Client",
                    value: clientFilter,
                    onChange: setClientFilter,
                    options: clients.map((value) => ({
                      value,
                      label: value === "all" ? "All" : value,
                    })),
                  },
                ]}
              />

              {filteredIncome.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No income matches this view.
                </Alert>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <IncomeList
                    income={categorizedIncome}
                    sectionTotals={sectionTotals}
                    prospectiveIncome={prospectiveIncome}
                    readOnly={true}
                    onDeleteIncome={noop}
                    onMarkReceived={noop}
                    onSkipIncome={noop}
                    onEditIncome={noop}
                    expandedSection={expandedSection}
                    onSectionChange={setExpandedSection}
                    showEmptySections={true}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SharedIncomePage;
